import '~/shared/styles/autofill.scss';
import { Form } from 'antd';
import classNames from 'classnames';
import { useCallback, useId, useRef, useState } from 'react';
import { type FieldValues, FormProvider, type SubmitErrorHandler } from 'react-hook-form';
import { toast } from 'react-toastify';

import FlashMessage from '~/shared/components/flash-message';
import FormNativeIdContext from '~/shared/components/form-block/form-native-id-context';
import { getPattern } from '~/shared/helpers/form-helpers';
import type { FormBlockPropsType } from '~/shared/types/form-type';

import useFormBlock from './use-form-block';

import '~/shared/components/form-block/form-block.scss';

export const FormBlock = <TFieldValues extends FieldValues>(props: FormBlockPropsType<TFieldValues>) => {
  const { handleSubmit, methods, formConfig, errors, control, t, watch, reset, initialValues, setValue, getValues } =
    useFormBlock<TFieldValues>(props);

  const showValidationErrorToast = useCallback(() => {
    toast.error(<FlashMessage title={null} message={t('error.validation.form-validation-error')} />);
  }, [t]);

  const handleInvalidSubmit: SubmitErrorHandler<TFieldValues> = () => {
    showValidationErrorToast();
  };
  const reactFormId = useId();
  const nativeFormId = `prana-form-${reactFormId.replace(/:/g, '')}`;

  const submitLock = useRef(false);
  const [submitPending, setSubmitPending] = useState(false);
  const submit = async () => {
    if (submitLock.current) return;
    submitLock.current = true;
    setSubmitPending(true);
    try {
      await handleSubmit((data, event) => props.onSubmit(data, event, { reset, initialValues }), handleInvalidSubmit)();
    } finally {
      submitLock.current = false;
      setSubmitPending(false);
    }
  };

  return (
    <div className={classNames('form-block', props.className)}>
      <FormProvider {...methods}>
        <FormNativeIdContext.Provider value={nativeFormId}>
          <Form
            id={nativeFormId}
            onFinish={submit}
            onFinishFailed={showValidationErrorToast}
          >
            {getPattern<TFieldValues>({
              ...props,
              ...formConfig,
              submitPending,
              errors,
              t,
              watch,
              control,
              setValue,
              getValues,
            })}
          </Form>
        </FormNativeIdContext.Provider>
      </FormProvider>
    </div>
  );
};

export default FormBlock;
